import { Box, Flex, Select, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fmtExposurePct, fmtMoney } from './calc/formatters';
import {
  SCENARIO_METRICS,
  buildScenarioComparisonData,
} from './calc/buildScenarioComparisonData';
import { SCENARIO_META } from './calc/scenarioMeta';

function ScenarioComparisonChart({ scenarioMap }) {
  const [metricKey, setMetricKey] = useState(SCENARIO_METRICS[0].key);
  const metric =
    SCENARIO_METRICS.find((m) => m.key === metricKey) ?? SCENARIO_METRICS[0];
  const formatValue = metric.format === 'percent' ? fmtExposurePct : fmtMoney;

  const { data, activeKeys } = useMemo(
    () => buildScenarioComparisonData(scenarioMap, metric.field),
    [scenarioMap, metric.field]
  );

  return (
    <Box className="card" p={5} mt={6}>
      <Flex
        justify="space-between"
        align="center"
        gap={3}
        mb={4}
        flexWrap="wrap"
      >
        <Text fontWeight="semibold">Scenario comparison</Text>
        <Select
          w="260px"
          size="sm"
          value={metricKey}
          onChange={(e) => setMetricKey(e.target.value)}
        >
          {SCENARIO_METRICS.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </Select>
      </Flex>
      <ResponsiveContainer width="99%" height={360}>
        <LineChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
          <CartesianGrid stroke="#E0E8F0" vertical={false} />
          <XAxis
            dataKey="month"
            stroke="#E0E8F0"
            tick={{ fill: '#4A5568', fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis
            axisLine={false}
            width={72}
            tick={{ fill: '#4A5568', fontSize: 12 }}
            tickFormatter={formatValue}
          />
          <Tooltip
            formatter={(value, name) => [formatValue(value), name]}
            labelFormatter={(label) => `Month ${label}`}
          />
          <Legend iconType="circle" verticalAlign="top" align="right" />
          {activeKeys.map((key) => (
            <Line
              key={key}
              type="monotone"
              dot={false}
              dataKey={key}
              name={SCENARIO_META[key].label}
              stroke={SCENARIO_META[key].color}
              strokeWidth={key === 'base' ? 3 : 2}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

ScenarioComparisonChart.propTypes = {
  scenarioMap: PropTypes.object.isRequired,
};

export default ScenarioComparisonChart;
